/**
 * Per-lane brain status — AI is the desk default, Classic local math is the
 * fail-closed fallback. Every lane records which one ran last and, when it
 * fell back, why. The desk binds these for the bottom status strip and for the
 * fallback notice; nothing here writes the board.
 */

export const BRAIN_LANES = ["inbox", "generate", "schedule", "conflicts"] as const;
export type BrainLane = (typeof BRAIN_LANES)[number];

export type BrainLaneMode = "idle" | "ai" | "fallback";

export type BrainLaneState = {
  mode: BrainLaneMode;
  /** Why the lane fell back to local math. Null whenever AI ran. */
  lastFallbackReason: string | null;
  lastRunAtUtc: string | null;
};

export type BrainLaneStatus = Record<BrainLane, BrainLaneState>;

export const BRAIN_LANE_LABEL: Record<BrainLane, string> = {
  inbox: "Inbox",
  generate: "Generate",
  schedule: "Schedule",
  conflicts: "Conflicts",
};

const IDLE_LANE: BrainLaneState = { mode: "idle", lastFallbackReason: null, lastRunAtUtc: null };

export const EMPTY_BRAIN_LANES: BrainLaneStatus = {
  inbox: IDLE_LANE,
  generate: IDLE_LANE,
  schedule: IDLE_LANE,
  conflicts: IDLE_LANE,
};

export function isBrainLane(value: unknown): value is BrainLane {
  return BRAIN_LANES.includes(value as BrainLane);
}

function hydrateLane(raw: unknown): BrainLaneState {
  if (!raw || typeof raw !== "object") return IDLE_LANE;
  const row = raw as Partial<BrainLaneState>;
  const mode: BrainLaneMode = row.mode === "ai" || row.mode === "fallback" ? row.mode : "idle";
  const reason =
    mode === "fallback" && typeof row.lastFallbackReason === "string" && row.lastFallbackReason.trim()
      ? row.lastFallbackReason.trim()
      : null;
  const at = typeof row.lastRunAtUtc === "string" && row.lastRunAtUtc.trim() ? row.lastRunAtUtc : null;
  return { mode, lastFallbackReason: reason, lastRunAtUtc: at };
}

export function hydrateBrainLanes(raw?: unknown): BrainLaneStatus {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    inbox: hydrateLane(row.inbox),
    generate: hydrateLane(row.generate),
    schedule: hydrateLane(row.schedule),
    conflicts: hydrateLane(row.conflicts),
  };
}

/** Record one lane run. AI runs clear the fallback reason; fallback runs keep it. */
export function markBrainLane(
  status: BrainLaneStatus,
  lane: BrainLane,
  patch: { mode: BrainLaneMode; reason?: string | null; now?: Date },
): BrainLaneStatus {
  const now = patch.now ?? new Date();
  const reason = patch.mode === "fallback" ? (patch.reason?.trim() || "Local math ran — reason not recorded.") : null;
  return {
    ...status,
    [lane]: {
      mode: patch.mode,
      lastFallbackReason: reason,
      lastRunAtUtc: now.toISOString(),
    },
  };
}

/** Desk notice for a fallback — says which lane fell back and why. */
export function brainFallbackNotice(lane: BrainLane, reason: string): string {
  const why = reason.trim() || "AI brain unavailable.";
  return `BRAIN  ${BRAIN_LANE_LABEL[lane]} fell back to Classic — ${why}`;
}

/** Bottom status strip chip: `Schedule: AI` / `Schedule: Classic`. */
export function brainLaneChip(lane: BrainLane, state: BrainLaneState): string {
  const mode = state.mode === "ai" ? "AI" : state.mode === "fallback" ? "Classic" : "—";
  return `${BRAIN_LANE_LABEL[lane]}: ${mode}`;
}

export function brainLanesOnFallback(status: BrainLaneStatus): BrainLane[] {
  return BRAIN_LANES.filter((lane) => status[lane].mode === "fallback");
}

/** One-line summary for the strip tooltip / Settings. */
export function brainLanesSummary(status: BrainLaneStatus): string {
  const down = brainLanesOnFallback(status);
  if (!down.length) return "AI brain is running every lane.";
  return down
    .map((lane) => `${BRAIN_LANE_LABEL[lane]}: Classic — ${status[lane].lastFallbackReason ?? "reason not recorded."}`)
    .join(" · ");
}
